// 共用檔案選取區 — 拖放 + 點選 + 大小檢查。
//
// 2026-08-08: 全站有四個上傳點 (基金/專案文件 DocumentsPanel、簽核附件
// SignoffAttachments、建單時的暫存附件 PendingAttachments、人事文件
// EmployeeDocuments), 同一個動作四種行為:
//   · 只有 PendingAttachments 真的支援拖放
//   · DocumentsPanel **寫著**「點選 / 拖拉檔案到這裡」, 但那是個 <label> 包 hidden
//     input —— label 不處理 drop 事件, 真的拖檔進去瀏覽器會直接開啟該檔、離開頁面,
//     抽屜裡填到一半的東西全沒了。 承諾了做不到的事, 而且代價是掉資料。
//   · 另外兩個只有一顆按鈕, 而且四個地方都沒告訴使用者上限是多少
//     (後端其實是 100MB / 25MB / 25MB 三種)
//
// 這支收斂的是**互動方式**: 拖放 + 點選 + 明講上限 + 一致的錯誤訊息。 各處的上限
// 仍由呼叫端傳 (文件 100MB vs 附件 25MB 是有意的差異), 但一定顯示出來。
import { useRef, useState } from 'react';
import { formatBytes } from '../../lib/format.ts';

export function FileDropZone({
  onFiles,
  maxBytes,
  multiple = false,
  accept,
  disabled = false,
  hint,
  /** 已選好的檔案 — 有值時顯示在框裡 (單檔情境用)。 */
  selected,
}: {
  onFiles: (files: File[]) => void;
  maxBytes: number;
  multiple?: boolean;
  accept?: string;
  disabled?: boolean;
  /** 上限以外要補充的說明 (例: 直接上傳到 Azure Blob)。 */
  hint?: string;
  selected?: File | null;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const take = (incoming: FileList | null) => {
    if (disabled || !incoming || incoming.length === 0) return;
    setErr(null);
    const ok: File[] = [];
    const tooBig: string[] = [];
    for (const f of Array.from(incoming)) {
      if (f.size > maxBytes) tooBig.push(`${f.name} (${formatBytes(f.size)})`);
      else ok.push(f);
    }
    if (tooBig.length > 0) {
      setErr(`超過 ${formatBytes(maxBytes)} 上限，未加入：${tooBig.join('、')}`);
    }
    if (ok.length > 0) onFiles(multiple ? ok : [ok[0] as File]);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        // ★ preventDefault 是關鍵 —— 沒有它, 瀏覽器的預設行為是「開啟這個檔案」,
        //   等於把使用者導離目前頁面 (填到一半的表單就沒了)。
        onDragOver={(e) => {
          if (disabled) return;
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          if (disabled) return;
          e.preventDefault();
          setDragOver(false);
          take(e.dataTransfer.files);
        }}
        className={`w-full rounded border border-dashed px-3 py-4 text-center text-sm transition ${
          disabled
            ? 'border-slate-200 text-slate-400 cursor-not-allowed'
            : dragOver
              ? 'border-blue-400 bg-blue-50 text-blue-700 cursor-pointer'
              : 'border-slate-300 text-slate-500 hover:border-slate-400 hover:bg-slate-50 cursor-pointer'
        }`}
      >
        {selected ? (
          <div className="text-sm">
            <div className="font-medium text-slate-800 truncate" title={selected.name}>
              {selected.name}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {formatBytes(selected.size)} · {selected.type || '未知格式'}
            </div>
          </div>
        ) : (
          <>
            📎 把檔案拖到這裡，或<span className="underline">點此選擇</span>
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        accept={accept}
        disabled={disabled}
        className="hidden"
        onChange={(e) => take(e.target.files)}
      />
      <p className="text-[11px] text-slate-400 mt-1">
        上限 {formatBytes(maxBytes)}
        {hint ? ` · ${hint}` : ''}
      </p>
      {err && <p className="text-[11px] text-red-600 mt-1">{err}</p>}
    </div>
  );
}
